export function money(value: number, compact = true): string {
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(value);
  if (compact && absolute >= 1_000_000_000) return `${sign}$${(absolute / 1_000_000_000).toFixed(2)}B`;
  if (compact && absolute >= 1_000_000) return `${sign}$${(absolute / 1_000_000).toFixed(2)}M`;
  if (compact && absolute >= 1_000) return `${sign}$${(absolute / 1_000).toFixed(2)}K`;
  return `${sign}$${absolute.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function wealthMoney(value: number): string {
  const numeric = Number.isFinite(value) ? value : 0;
  const sign = numeric < 0 ? '-' : '';
  const absolute = Math.abs(numeric);
  if (absolute >= 1_000_000_000_000) return `${sign}$${(absolute / 1_000_000_000_000).toFixed(2)}T`;
  if (absolute >= 1_000_000_000) return `${sign}$${(absolute / 1_000_000_000).toFixed(2)}B`;
  if (absolute >= 1_000_000) return `${sign}$${(absolute / 1_000_000).toFixed(2)}M`;
  if (absolute >= 1_000) return `${sign}$${(absolute / 1_000).toFixed(2)}K`;
  return `${sign}$${absolute.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export type FinancialDisplayUnit = '' | 'K' | 'M' | 'B';

const FINANCIAL_UNIT_SCALE: Record<FinancialDisplayUnit, number> = {
  '': 1,
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
};

/**
 * Select one unit for a whole financial table so values can be compared at a glance.
 * The first scaled unit is used once a raw value would exceed nine digits.
 */
export function financialUnitFor(values: number[]): FinancialDisplayUnit {
  const maximum = Math.max(0, ...values.map((value) => Math.abs(Number.isFinite(value) ? value : 0)));
  if (maximum >= 1_000_000_000_000_000) return 'B';
  if (maximum >= 1_000_000_000_000) return 'M';
  if (maximum >= 1_000_000_000) return 'K';
  return '';
}

export function financialMoney(value: number, unit: FinancialDisplayUnit = ''): string {
  const sign = value < 0 ? '-' : '';
  const absolute = Math.abs(Number.isFinite(value) ? value : 0);
  const scaled = absolute / FINANCIAL_UNIT_SCALE[unit];
  const digits = scaled > 0 && scaled < 1 ? 1 : 0;
  const formatted = digits
    ? scaled.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : Math.floor(scaled).toLocaleString('en-US');
  return `${sign}$${formatted}${unit}`;
}

const SUMMARY_UNITS = [
  { threshold: 1_000_000_000_000, suffix: 'T' },
  { threshold: 1_000_000_000, suffix: 'B' },
  { threshold: 1_000_000, suffix: 'M' },
  { threshold: 1_000, suffix: 'K' },
] as const;

export function summaryNumber(value: number, prefix = ''): string {
  const numeric = Number.isFinite(value) ? value : 0;
  const sign = numeric < 0 ? '-' : '';
  const absolute = Math.abs(numeric);
  const unit = SUMMARY_UNITS.find((candidate) => absolute >= candidate.threshold);
  const scaled = unit ? absolute / unit.threshold : absolute;
  const integerDigits = Math.max(1, Math.floor(scaled).toString().length);
  const maximumFractionDigits = unit ? Math.max(0, 6 - integerDigits) : 0;
  const formatted = scaled.toLocaleString('en-US', { maximumFractionDigits });
  return `${sign}${prefix}${formatted}${unit ? unit.suffix : ''}`;
}

export const summaryMoney = (value: number) => summaryNumber(value, '$');

export function fuelMillions(value: number): string {
  const numeric = Number.isFinite(value) ? Math.max(0, value) : 0;
  return `${(numeric / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
}

export function fuelConsumption(value: number): string {
  const numeric = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  if (numeric < 1_000_000_000) return numeric.toLocaleString('zh-CN');
  return `${(numeric / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`;
}

export const percent = (value: number, digits = 0) => `${(Math.min(1, Math.max(0, value)) * 100).toFixed(digits)}%`;
export const financialMultiple = (value: number | null) => value !== null && Number.isFinite(value) ? `${value.toFixed(2)}x` : '—';
export const integer = (value: number) => Math.round(value).toLocaleString('zh-CN');
export const kilometers = (value: number) => `${Math.round(value).toLocaleString('zh-CN')} km`;
